// frontend/src/components/plot/DeviceList.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePlotDetailContext } from '../../context/PlotDetailContext';
import { getStatusBadge } from '../../constants/plotConstants';

const NAV_ACTION_BTN_STYLE = {
  background: '#f1f5f9',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  color: '#334155',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'all 0.15s ease'
};

export default function DeviceList({
  devices: propDevices,
  visibleDeviceIds: propVisibleDeviceIds,
  onToggleDevice,
  onAddDevice,
  onOpenModal,
  isDeviceDetailPage = false,
  onDeviceUpdated = () => {}
}) {
  const context = usePlotDetailContext();
  const navigate = useNavigate();
  const { farmId, plotId } = useParams();

  const devices = propDevices || context?.devices || [];
  const visibleDeviceIds = propVisibleDeviceIds || context?.visibleDeviceIds || [];
  const toggleVisibility = onToggleDevice || context?.toggleDeviceVisibility || context?.toggleDevice || (() => {});
  const fetchPlotDetails = context?.fetchPlotDetails || (() => {});
  const loading = context?.loading || false;
  const plot = context?.plot;

  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  if (loading) {
    return <div style={{ padding: '12px', color: '#64748b' }}>Loading hardware & proxies...</div>;
  }

  const handleAddClick = () => {
    if (onAddDevice) {
      onAddDevice(plot);
    } else if (onOpenModal) {
      onOpenModal();
    }
  };

  const startEditing = (dev, e) => {
    e.stopPropagation();
    setEditingDeviceId(dev.id || dev.device_id);
    setEditName(dev.name || '');
    setEditDescription(dev.description || '');
  };

  const saveDeviceEdit = async (deviceId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSaving(true);
    try {
      await axios.patch(`/api/devices/${deviceId}`, {
        name: editName,
        description: editDescription
      }, { withCredentials: true });

      if (fetchPlotDetails) await fetchPlotDetails();
      onDeviceUpdated();
      setEditingDeviceId(null);
    } catch (err) {
      console.error('Failed to update device details:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToSiteDevices = () => {
    navigate(`/dashboard/farm/${farmId}/plot/${plotId}/device`);
  };

  const handleBackToPlot = () => {
    navigate(`/dashboard/farm/${farmId}/plot/${plotId}`);
  };

  return (
    <div style={{ marginBottom: '0' }}>
      {/* Section Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isDeviceDetailPage && (
            <button
              type="button"
              onClick={handleBackToPlot}
              style={NAV_ACTION_BTN_STYLE}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
            >
              ← Back to Plot
            </button>
          )}
          <h4 style={{ margin: '0', fontSize: '14px', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Assigned Devices & Stations ({devices.length})
          </h4>
        </div>

        {!isDeviceDetailPage && (
          <button
            type="button"
            onClick={handleGoToSiteDevices}
            style={NAV_ACTION_BTN_STYLE}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
          >
            Go to Site Devices →
          </button>
        )}
      </div>

      {/* Devices Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
        {devices.map((dev) => {
          const devIdStr = String(dev.id || dev.device_id || '');
          const isChecked = visibleDeviceIds.some((id) => String(id) === devIdStr);
          const statusBadge = getStatusBadge(dev.status);

          // Use explicit proxy property provided directly by PlotDetailContext
          const isProxy = Boolean(dev.isProxy);
          const isEditing = editingDeviceId === dev.id;

          return (
            <div
              key={devIdStr}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: `2px ${isProxy ? 'dashed' : 'solid'} ${isChecked ? (dev.color || '#166534') : '#e2e8f0'}`,
                background: isChecked ? '#f8fafc' : '#ffffff',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 600, color: dev.color || '#1e293b', flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleVisibility(dev.id)}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />

                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        style={{ padding: '2px 6px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                      />
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                        style={{ padding: '2px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  ) : (
                    <span>{dev.name} {dev.location ? `(${dev.location})` : ''}</span>
                  )}
                </label>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isProxy ? (
                    <span style={{ fontSize: '10px', fontWeight: 'bold', background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px' }}>
                      REGIONAL PROXY
                    </span>
                  ) : (
                    <span style={{ fontSize: '10px', fontFamily: 'monospace', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px' }}>
                      HEX: {devIdStr}
                    </span>
                  )}

                  {statusBadge && !isProxy && (
                    <span style={{ fontSize: '10px', fontWeight: 'bold', background: statusBadge.bg, color: statusBadge.text, padding: '2px 6px', borderRadius: '4px' }}>
                      {statusBadge.label}
                    </span>
                  )}

                  {!isProxy && (
                    isEditing ? (
                      <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                        <button onClick={(e) => saveDeviceEdit(dev.id, e)} disabled={saving} style={{ fontSize: '10px', padding: '2px 6px', background: '#166534', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                        <button onClick={(e) => { e.stopPropagation(); setEditingDeviceId(null); }} style={{ fontSize: '10px', padding: '2px 6px', background: '#64748b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={(e) => startEditing(dev, e)} style={{ fontSize: '10px', padding: '2px 6px', background: 'transparent', color: '#2563eb', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
                    )
                  )}
                </div>
              </div>

              {!isEditing && dev.description && (
                <p style={{ margin: '6px 0 0 24px', fontSize: '12px', color: '#64748b' }}>
                  {dev.description}
                </p>
              )}
            </div>
          );
        })}

        {/* Provisioning Add Physical Device card */}
        {(onAddDevice || onOpenModal) && (
          <div
            onClick={handleAddClick}
            role="button"
            tabIndex={0}
            style={{
              padding: '16px',
              borderRadius: '8px',
              border: '2px dashed #cbd5e1',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              minHeight: '80px',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#166534'; e.currentTarget.style.background = '#f0fdf4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#166534', marginBottom: '2px' }}>+</span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>Add Physical Device</span>
            <span style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', marginTop: '2px' }}>
              Provision hardware sensors for this plot
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
